import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useAuth, API } from "../App";
import axios from "axios";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { ArrowLeft, Download, Share2, ShoppingBag, ExternalLink } from "lucide-react";
import { toast } from "sonner";

export default function ResultPage() {
  const { resultId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [result, setResult] = useState(null);
  const [garment, setGarment] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchResult();
  }, [resultId]);

  const fetchResult = async () => {
    try {
      const response = await axios.get(`${API}/try-on/result/${resultId}`, {
        withCredentials: true
      });
      setResult(response.data);
      
      // Fetch garment details for purchase URL
      if (response.data.garment_id) {
        const garmentResponse = await axios.get(`${API}/garments/${response.data.garment_id}`);
        setGarment(garmentResponse.data);
      }
    } catch (error) {
      toast.error("Result not found");
      navigate("/dashboard");
    } finally {
      setLoading(false);
    }
  };

  const handleOrder = () => {
    if (garment?.purchase_url) {
      window.open(garment.purchase_url, '_blank');
    } else {
      window.open('https://tailorglam.com', '_blank');
    }
  };

  const handleDownload = () => {
    if (!result?.result_image) return;
    
    const link = document.createElement('a');
    link.href = `data:image/png;base64,${result.result_image}`;
    link.download = `tailorglam-${result.garment_name || 'tryon'}.png`;
    link.click();
    toast.success("Image downloaded!");
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'My TailorGlam Try-On',
          text: `Check out how I look in ${result?.garment_name}!`,
          url: window.location.href
        });
      } catch (error) {
        // User cancelled or error
      }
    } else {
      navigator.clipboard.writeText(window.location.href);
      toast.success("Link copied to clipboard!");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <div className="w-10 h-10 spinner"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A]" data-testid="result-page">
      <Navbar />
      
      <main className="pt-24 md:pt-32 pb-24">
        <div className="container-custom">
          {/* Back Button */}
          <Link 
            to="/dashboard" 
            className="inline-flex items-center gap-2 text-gray-400 hover:text-white mb-8"
            data-testid="back-link"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Dashboard
          </Link>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
            {/* Result Image */}
            <div className="result-display" data-testid="result-image-container">
              <img
                src={`data:image/png;base64,${result?.result_image}`}
                alt={result?.garment_name}
                className="w-full h-auto"
              />
            </div>

            {/* Details */}
            <div className="flex flex-col justify-center">
              <p className="caption text-[#FF1493] mb-4">Your Try-On Result</p>
              <h1 className="heading-lg mb-4">{result?.garment_name}</h1>
              <p className="text-gray-400 mb-8">
                Generated on {new Date(result?.created_at).toLocaleDateString('en-US', {
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </p>

              {/* Actions */}
              <div className="space-y-4 mb-12">
                <button 
                  onClick={handleOrder}
                  className="btn-primary w-full"
                  data-testid="start-order-btn"
                >
                  <ShoppingBag className="w-5 h-5" />
                  Start My Custom Order
                  <ExternalLink className="w-4 h-4" />
                </button>
                
                <div className="grid grid-cols-2 gap-4">
                  <button 
                    onClick={handleDownload}
                    className="btn-secondary"
                    data-testid="download-btn"
                  >
                    <Download className="w-4 h-4" />
                    Download
                  </button>
                  <button 
                    onClick={handleShare}
                    className="btn-secondary"
                    data-testid="share-btn"
                  >
                    <Share2 className="w-4 h-4" />
                    Share
                  </button>
                </div>
              </div>

              {/* Next Steps */}
              <div className="border-t border-white/10 pt-8">
                <h3 className="font-heading text-lg mb-4 text-white">What's Next?</h3>
                <ul className="space-y-3 text-gray-400 text-sm">
                  <li className="flex items-start gap-3">
                    <span className="w-6 h-6 bg-[#FF1493]/20 flex items-center justify-center flex-shrink-0 text-[#FF1493] font-mono text-xs">1</span>
                    Click "Start My Custom Order" to begin the ordering process
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-6 h-6 bg-[#FF1493]/20 flex items-center justify-center flex-shrink-0 text-[#FF1493] font-mono text-xs">2</span>
                    Our tailor will reach out to discuss measurements and details
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-6 h-6 bg-[#FF1493]/20 flex items-center justify-center flex-shrink-0 text-[#FF1493] font-mono text-xs">3</span>
                    Receive your custom-made garment delivered to your door
                  </li>
                </ul>
              </div>

              {/* More Try-Ons */}
              <div className="mt-8 p-6 bg-[#141414] border border-white/10">
                <p className="font-medium mb-2 text-white">Want to try more?</p>
                <p className="text-sm text-gray-400 mb-4">
                  {user?.is_subscribed 
                    ? `You have ${50 - (user?.generations_used || 0)} generations left this month.`
                    : "Upgrade to Pro for 50 generations per month."
                  }
                </p>
                <Link to="/studio">
                  <button className="btn-secondary text-sm h-10" data-testid="try-another-btn">
                    Try Another Design
                  </button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
